import { env } from '@/config/env';
import { getTenant } from '@/stores/tenant';
import { useAuthStore, type SessionUser } from '@/stores/auth';

/**
 * Autenticación WRAPEADA por el backend (patrón C4T / BlueSentinel).
 *
 * El navegador NUNCA habla con Keycloak: manda usuario+contraseña al backend
 * (`POST /api/autenticacion/login`) y este ejecuta el grant OAuth2 contra el
 * realm del tenant con su client confidencial. Los tokens devueltos son JWTs
 * de Keycloak en passthrough; el resto del API los exige como `Bearer`.
 *
 * Endpoints del backend (form-urlencoded, como el propio Keycloak):
 *   POST /autenticacion/login      username, password      -> tokens
 *   POST /autenticacion/refrescos  refresh_token           -> tokens renovados
 *   POST /autenticacion/cierres    refresh_token           -> {success}
 *   GET  /me                       (Bearer)                -> usuario de sesión
 *
 * Persistencia: sessionStorage (clave `ambulancias.sesion`) para sobrevivir a
 * recargas de página sin exponer los tokens entre pestañas/ventanas.
 *
 * Modo `mock` (VITE_AUTH_MODE=mock): sesión demo local sin backend, para poder
 * desarrollar/revisar la UI (pixel-perfect) de forma aislada.
 */

const STORAGE_KEY = 'ambulancias.sesion';

/** Margen (ms) antes de la caducidad real a partir del cual refrescamos. */
const REFRESH_SKEW_MS = 30_000;

interface StoredSession {
  accessToken: string;
  refreshToken: string | null;
  /** epoch ms en el que caduca el access token. */
  expiresAt: number;
}

/** Respuesta estándar del backend: { success, data, messages }. */
interface ApiEnvelope<T> {
  success: boolean;
  data: T | null;
  messages: string[];
}

/** Tokens tal como los emite Keycloak (passthrough del backend). */
interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
  refresh_expires_in?: number;
}

interface MeData {
  username: string;
  nombreCompleto: string;
  roles: string[];
  tenant: { code: string; nombre: string };
}

/* ------------------------------------------------------------------ */
/* Transporte propio (fetch plano): esta capa ES el proveedor de       */
/* tokens, no puede depender del cliente API (evita ciclos).           */
/* ------------------------------------------------------------------ */

async function postForm<T>(path: string, form: Record<string, string>): Promise<ApiEnvelope<T>> {
  const res = await fetch(`${env.apiBase}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
      'X-TenantID': getTenant(),
    },
    body: new URLSearchParams(form).toString(),
  });
  const body = (await res.json().catch(() => null)) as ApiEnvelope<T> | null;
  if (!res.ok || !body) {
    return { success: false, data: null, messages: body?.messages ?? [`http_${res.status}`] };
  }
  return body;
}

async function fetchMe(accessToken: string): Promise<SessionUser | null> {
  const res = await fetch(`${env.apiBase}/me`, {
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-TenantID': getTenant(),
    },
  });
  if (!res.ok) return null;
  const body = (await res.json().catch(() => null)) as ApiEnvelope<MeData> | null;
  if (!body?.success || !body.data) return null;
  return {
    username: body.data.username,
    name: body.data.nombreCompleto,
    roles: body.data.roles ?? [],
  };
}

/* ------------------------------------------------------------------ */
/* Persistencia en sessionStorage                                       */
/* ------------------------------------------------------------------ */

function saveSession(s: StoredSession | null): void {
  if (s) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  else sessionStorage.removeItem(STORAGE_KEY);
}

function readSession(): StoredSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredSession) : null;
  } catch {
    return null;
  }
}

function applyTokens(tokens: TokenResponse): StoredSession {
  const session: StoredSession = {
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token ?? null,
    expiresAt: Date.now() + tokens.expires_in * 1000,
  };
  saveSession(session);
  useAuthStore.getState().set({ token: session.accessToken });
  return session;
}

function clearSession(): void {
  saveSession(null);
  useAuthStore.getState().reset();
}

/* ------------------------------------------------------------------ */
/* API pública de esta capa                                             */
/* ------------------------------------------------------------------ */

/**
 * Bootstrap al arrancar la SPA: restaura la sesión de sessionStorage.
 * Si el access token sigue vivo lo valida contra /me; si caducó pero hay
 * refresh token, refresca; en cualquier otro caso deja la app sin sesión
 * (la guarda de rutas mandará a /login).
 */
export async function initAuth(): Promise<void> {
  const store = useAuthStore.getState();

  if (env.authMode === 'mock') {
    store.set({ initialized: true, authenticated: false });
    return;
  }

  const stored = readSession();
  if (!stored) {
    store.set({ initialized: true, authenticated: false });
    return;
  }

  try {
    let session = stored;
    if (Date.now() >= session.expiresAt - REFRESH_SKEW_MS) {
      if (!session.refreshToken) throw new Error('token caducado sin refresh');
      const r = await postForm<TokenResponse>('/autenticacion/refrescos', {
        refresh_token: session.refreshToken,
      });
      if (!r.success || !r.data) throw new Error('refresco rechazado');
      session = applyTokens(r.data);
    }
    const user = await fetchMe(session.accessToken);
    if (!user) throw new Error('sesion invalida');
    store.set({
      initialized: true,
      authenticated: true,
      token: session.accessToken,
      user,
    });
  } catch {
    clearSession();
    store.set({ initialized: true, authenticated: false });
  }
}

/**
 * Login con usuario y contraseña contra el backend. Lanza Error con mensaje
 * legible si las credenciales no son válidas o el servicio no responde.
 */
export async function login(username: string, password: string): Promise<void> {
  if (env.authMode === 'mock') {
    useAuthStore.getState().set({
      authenticated: true,
      token: 'mock-token',
      user: {
        username: 'coordinacion.seapa',
        name: 'M. Ríos',
        email: 'coordinacion@pozuelo.es',
        roles: ['coordinacion'],
      },
    });
    return;
  }

  const r = await postForm<TokenResponse>('/autenticacion/login', { username, password });
  if (!r.success || !r.data) {
    const key = r.messages[0] ?? '';
    throw new Error(
      key.includes('credenciales')
        ? 'Usuario o contraseña incorrectos.'
        : 'No se pudo iniciar sesión. Inténtalo de nuevo.',
    );
  }

  const session = applyTokens(r.data);
  const user = await fetchMe(session.accessToken);
  useAuthStore.getState().set({
    authenticated: true,
    token: session.accessToken,
    user: user ?? { username, name: username, roles: [] },
  });
}

/** Cierra sesión: revoca el refresh token en el backend y limpia el estado local. */
export async function logout(): Promise<void> {
  if (env.authMode === 'mock') {
    useAuthStore.getState().reset();
    return;
  }
  const stored = readSession();
  if (stored?.refreshToken) {
    // Best-effort: aunque la revocación falle, la sesión local se limpia.
    await postForm('/autenticacion/cierres', { refresh_token: stored.refreshToken }).catch(() => undefined);
  }
  clearSession();
}

/** Promesa en vuelo para no lanzar refrescos concurrentes. */
let refreshInFlight: Promise<void> | null = null;

/**
 * Garantiza un access token fresco antes de una llamada API (la llama el
 * cliente `lib/api.ts`). Si quedan <30s de vida y hay refresh token, refresca;
 * si el refresco falla, resetea la sesión (la guarda redirige a /login).
 */
export async function refreshToken(): Promise<void> {
  if (env.authMode === 'mock') return;
  const stored = readSession();
  if (!stored?.refreshToken) return;
  if (Date.now() < stored.expiresAt - REFRESH_SKEW_MS) return;

  refreshInFlight ??= (async () => {
    try {
      const r = await postForm<TokenResponse>('/autenticacion/refrescos', {
        refresh_token: stored.refreshToken as string,
      });
      if (!r.success || !r.data) throw new Error('refresco rechazado');
      applyTokens(r.data);
    } catch {
      clearSession();
    } finally {
      refreshInFlight = null;
    }
  })();
  await refreshInFlight;
}
